// Lightweight game audio: two looping music beds (menu / gameplay) that crossfade, plus
// fire-and-forget one-shot SFX. Music/SFX volume + mute persist to localStorage. Nothing
// plays until the first user gesture (browser autoplay policy); a track requested before
// then is queued and starts on that gesture. All files live under /public/audio.

type Track = 'menu' | 'game';

// SFX event names the game fires. Files are added under public/audio/sfx/ as they are made;
// a name with no file simply no-ops, so call sites can be wired ahead of the assets.
export type SfxName = 'click' | 'build' | 'move' | 'attack' | 'coin' | 'endturn' | 'offer' | 'win' | 'lose' | 'war' | 'achieve' | 'recruit' | 'allybreak' | 'tick' | 'yourturn';

const MUSIC_SRC: Record<Track, string> = {
  menu: 'audio/menu-music.mp3',
  game: 'audio/game-music.mp3',
};

const SFX_SRC: Partial<Record<SfxName, string>> = {
  click: 'audio/sfx/click.mp3', // UI button press
  attack: 'audio/sfx/attack.mp3', // swords clash
  move: 'audio/sfx/move.mp3', // troops march
  // 'build' and 'recruit' are synthesized (buildThunk / recruitSwoosh), no asset needed
};

// per-SFX loudness trim, relative to the global SFX volume. The UI click fires constantly, so it
// sits well under the game-action sounds, kept soft on purpose.
const SFX_GAIN: Partial<Record<SfxName, number>> = {
  click: 0.55,
  war: 0.7, // the synthesized war horn, dramatic but not startling
  achieve: 0.6, // the achievement-unlock chime
  recruit: 0.5, // the synthesized sword-draw when a unit is trained
  build: 0.6, // the synthesized construction thunk when a building goes up
  coin: 0.5, // the synthesized purchase "cha-ching" when a cosmetic is bought
  allybreak: 0.65, // the synthesized snapped-bond sting when an alliance is broken
  tick: 0.5, // the synthesized clock tick in the last seconds of a turn timer
};

const LS_KEY = 'wi.audio.v1';
const FADE_MS = 900;

const clamp01 = (n: number): number => Math.max(0, Math.min(1, Number.isFinite(n) ? n : 0));

interface Settings {
  music: number; // 0..1
  sfx: number; // 0..1
  muted: boolean;
}

function loadSettings(): Settings {
  try {
    const s = JSON.parse(localStorage.getItem(LS_KEY) ?? '{}') as Partial<Settings>;
    return { music: clamp01(s.music ?? 0.55), sfx: clamp01(s.sfx ?? 0.8), muted: !!s.muted };
  } catch {
    return { music: 0.55, sfx: 0.8, muted: false };
  }
}

class AudioManager {
  private beds = {} as Record<Track, HTMLAudioElement>;
  private sfxEls = new Map<SfxName, HTMLAudioElement>();
  private cur: Track | null = null;
  private pending: Track | null = null;
  private unlocked = false;
  private started = false;
  private settings = loadSettings();
  private ducked = false; // an ad is playing: music stays silent even across tab switches
  private fades = new Map<HTMLAudioElement, number>(); // active fade interval ids
  private ac: AudioContext | null = null; // lazily created for synthesized SFX (the war horn), no asset needed
  private warImpulse: AudioBuffer | null = null; // cached convolver impulse (hall reverb) for the war sting
  private warNoise: AudioBuffer | null = null; // cached white-noise burst for the war-sting crash

  /** Create the audio elements and arm the first-gesture unlock. Safe to call once. */
  init(): void {
    if (this.started) return;
    this.started = true;
    (Object.keys(MUSIC_SRC) as Track[]).forEach((t) => {
      const a = new Audio(MUSIC_SRC[t]);
      a.loop = true;
      // both beds are lazy: playMusic() flips preload to 'auto' the moment a bed actually starts, and the
      // mp3 streams progressively from there. Eagerly fetching a bed at boot competed with the sprites for
      // first-seconds bandwidth, and on the instant-play path the menu bed never even plays.
      a.preload = 'none';
      a.volume = 0;
      a.muted = this.settings.muted; // iOS Safari ignores volume writes; the muted flag works everywhere
      this.beds[t] = a;
    });
    for (const [name, src] of Object.entries(SFX_SRC)) {
      if (!src) continue;
      const a = new Audio(src);
      a.preload = 'auto';
      this.sfxEls.set(name as SfxName, a);
    }
    const unlock = (): void => this.onGesture();
    window.addEventListener('pointerdown', unlock, { capture: true });
    window.addEventListener('keydown', unlock, { capture: true });
    window.addEventListener('touchstart', unlock, { capture: true });
    // be a good citizen: hush music while the tab is in the background, resume on return
    document.addEventListener('visibilitychange', () => this.onVisibility());
  }

  private onGesture(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    if (this.pending) {
      const t = this.pending;
      this.pending = null;
      this.playMusic(t);
    }
  }

  private onVisibility(): void {
    if (!this.cur) return;
    const bed = this.beds[this.cur];
    if (document.hidden) bed.pause();
    // `ducked` guard: returning to the tab MID-AD must not resume music over the ad
    else if (!this.settings.muted && !this.ducked) void bed.play().catch(() => {});
  }

  /** Crossfade to a music bed (queued until the first gesture if audio is still locked). */
  playMusic(track: Track): void {
    if (!this.unlocked) {
      this.pending = track;
      return;
    }
    if (this.cur === track) return;
    const prev = this.cur ? this.beds[this.cur] : null;
    const next = this.beds[track];
    this.cur = track;
    const target = this.settings.muted ? 0 : this.settings.music;
    next.volume = 0;
    next.muted = this.settings.muted;
    if (next.preload === 'none') next.preload = 'auto'; // lazily-created bed: start its fetch now
    void next.play().catch(() => {});
    this.rampTo(next, target);
    if (prev && prev !== next) this.rampTo(prev, 0, () => prev.pause());
  }

  stopMusic(): void {
    if (!this.cur) return;
    const bed = this.beds[this.cur];
    this.cur = null;
    this.rampTo(bed, 0, () => bed.pause());
  }

  /** Transiently pause/resume music for the duration of an ad, without touching the mute setting. */
  duck(on: boolean): void {
    this.ducked = on; // remembered so a tab switch during the ad cannot resume music over it
    if (!this.cur) return;
    const bed = this.beds[this.cur];
    if (on) bed.pause();
    else if (!this.settings.muted && !document.hidden) void bed.play().catch(() => {});
  }

  /** Play a one-shot SFX (overlapping-safe via a cloned node). No-ops if muted or file absent. */
  sfx(name: SfxName): void {
    if (this.settings.muted || this.settings.sfx <= 0) return;
    if (name === 'war') return this.warHorn(); // synthesized, no asset file
    if (name === 'achieve') return this.achieveChime(); // synthesized
    if (name === 'recruit') return this.recruitSwoosh(); // synthesized sword-draw (a unit is trained)
    if (name === 'build') return this.buildThunk(); // synthesized construction thunk (a building goes up)
    if (name === 'coin') return this.coinChime(); // synthesized purchase chime (a cosmetic is bought)
    if (name === 'allybreak') return this.allyBreakSnap(); // synthesized snapped-bond sting (an alliance ends)
    if (name === 'tick') return this.clockTick(); // synthesized clock tick (turn timer running out)
    if (name === 'yourturn') return this.yourTurnChime(); // synthesized herald chime (the turn came back to you)
    const base = this.sfxEls.get(name);
    if (!base) return;
    const node = base.cloneNode(true) as HTMLAudioElement;
    node.volume = clamp01(this.settings.sfx * (SFX_GAIN[name] ?? 1));
    void node.play().catch(() => {});
  }

  private ctx(): AudioContext | null {
    if (!this.unlocked) return null; // autoplay policy: need a prior user gesture
    if (!this.ac) {
      try {
        this.ac = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      } catch {
        return null;
      }
    }
    if (this.ac.state === 'suspended') void this.ac.resume().catch(() => {});
    return this.ac;
  }

  // a hall-reverb impulse (exponentially-decaying noise), built once, so the war sting sits in a cinematic space
  private impulse(ac: AudioContext): AudioBuffer {
    if (this.warImpulse) return this.warImpulse;
    const len = Math.floor(ac.sampleRate * 2);
    const buf = ac.createBuffer(2, len, ac.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 2.6; // Math.random OK: audio synthesis, not game state
    }
    return (this.warImpulse = buf);
  }
  private noise(ac: AudioContext): AudioBuffer {
    if (this.warNoise) return this.warNoise;
    const len = Math.floor(ac.sampleRate * 0.7);
    const buf = ac.createBuffer(1, len, ac.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    return (this.warNoise = buf);
  }

  /** A broken alliance: a dry SNAP (the pact tearing) followed by two detuned voices sliding DOWN a
   *  tritone, the classic "something just went wrong" interval, short and sour rather than epic. */
  private allyBreakSnap(): void {
    const ac = this.ctx();
    if (!ac) return;
    const t0 = ac.currentTime;
    const master = ac.createGain();
    master.gain.value = clamp01(this.settings.sfx * (SFX_GAIN.allybreak ?? 0.65));
    const comp = ac.createDynamicsCompressor();
    comp.threshold.value = -12;
    comp.ratio.value = 10;
    comp.attack.value = 0.002;
    comp.release.value = 0.2;
    comp.connect(ac.destination);
    master.connect(comp);
    // 1. the snap: a short mid-band noise crack
    const snap = ac.createBufferSource();
    snap.buffer = this.noise(ac);
    const bp = ac.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 2100;
    bp.Q.value = 1.1;
    const sg = ac.createGain();
    sg.gain.setValueAtTime(0.85, t0);
    sg.gain.exponentialRampToValueAtTime(0.001, t0 + 0.14);
    snap.connect(bp).connect(sg).connect(master);
    snap.start(t0);
    snap.stop(t0 + 0.16);
    // 2. the sour slide: two slightly detuned saws falling a tritone together
    const lp = ac.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(1600, t0);
    lp.frequency.linearRampToValueAtTime(500, t0 + 0.8);
    const vg = ac.createGain();
    vg.gain.setValueAtTime(0.0001, t0 + 0.05);
    vg.gain.linearRampToValueAtTime(0.34, t0 + 0.12);
    vg.gain.exponentialRampToValueAtTime(0.001, t0 + 0.85);
    lp.connect(vg).connect(master);
    for (const det of [0, 7]) {
      const o = ac.createOscillator();
      o.type = 'sawtooth';
      o.detune.value = det;
      o.frequency.setValueAtTime(392, t0 + 0.05); // G4 ...
      o.frequency.exponentialRampToValueAtTime(277.18, t0 + 0.7); // ... sinking to C#4, a tritone below
      o.connect(lp);
      o.start(t0 + 0.05);
      o.stop(t0 + 0.9);
    }
  }

  /** An orchestral WAR sting (~2.6s, fully synthesized, no asset): a cymbal-ish crash + war-drum timpani hits
   *  under a swelling, detuned MINOR brass chord, run through a hall reverb and a limiter. Fired when war is
   *  declared on or by the player. */
  private warHorn(): void {
    const ac = this.ctx();
    if (!ac) return;
    const t0 = ac.currentTime;
    // master → limiter → out, with a parallel reverb send, so layered voices stay big but never clip
    const master = ac.createGain();
    master.gain.value = clamp01(this.settings.sfx * (SFX_GAIN.war ?? 0.7));
    const comp = ac.createDynamicsCompressor();
    comp.threshold.value = -10;
    comp.ratio.value = 12;
    comp.attack.value = 0.003;
    comp.release.value = 0.25;
    comp.connect(ac.destination);
    master.connect(comp);
    const conv = ac.createConvolver();
    conv.buffer = this.impulse(ac);
    const wet = ac.createGain();
    wet.gain.value = 0.32;
    master.connect(conv);
    conv.connect(wet);
    wet.connect(comp);

    // 1. crash: a bright, fast-decaying noise burst for the initial impact
    const crash = ac.createBufferSource();
    crash.buffer = this.noise(ac);
    const chp = ac.createBiquadFilter();
    chp.type = 'highpass';
    chp.frequency.value = 1400;
    const cg = ac.createGain();
    cg.gain.setValueAtTime(0.4, t0);
    cg.gain.exponentialRampToValueAtTime(0.001, t0 + 0.6);
    crash.connect(chp).connect(cg).connect(master);
    crash.start(t0);
    crash.stop(t0 + 0.7);

    // 2. war-drum timpani: three low pitched thumps that drive the sting
    const timp = (when: number, freq: number, gain: number): void => {
      const o = ac.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(freq * 1.8, when);
      o.frequency.exponentialRampToValueAtTime(freq, when + 0.09);
      const g = ac.createGain();
      g.gain.setValueAtTime(gain, when);
      g.gain.exponentialRampToValueAtTime(0.001, when + 0.45);
      o.connect(g).connect(master);
      o.start(when);
      o.stop(when + 0.5);
    };
    timp(t0, 62, 0.65);
    timp(t0 + 0.52, 55, 0.5);
    timp(t0 + 1.04, 62, 0.55);

    // 3. brass section: a low MINOR chord (root, minor 3rd, 5th, octave), two detuned saws per note for an
    //    ensemble body, a low-pass that opens on the swell then closes on the decay, over ~2.6s
    const lp = ac.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(480, t0);
    lp.frequency.linearRampToValueAtTime(2500, t0 + 0.7);
    lp.frequency.linearRampToValueAtTime(1300, t0 + 2.5);
    const brass = ac.createGain();
    brass.gain.setValueAtTime(0.0001, t0);
    brass.gain.exponentialRampToValueAtTime(0.42, t0 + 0.38); // swell in
    brass.gain.setValueAtTime(0.42, t0 + 1.7);
    brass.gain.exponentialRampToValueAtTime(0.0001, t0 + 2.6); // decay out
    lp.connect(brass);
    brass.connect(master);
    for (const f of [110, 130.81, 164.81, 220]) {
      for (const det of [-5, 5]) {
        const o = ac.createOscillator();
        o.type = 'sawtooth';
        o.frequency.value = f;
        o.detune.value = det;
        o.frequency.exponentialRampToValueAtTime(f * 0.99, t0 + 2.5); // a slow, ominous downward drift
        o.connect(lp);
        o.start(t0);
        o.stop(t0 + 2.65);
      }
    }
  }

  /** A bright ACHIEVEMENT chime (~0.9s, synthesized): an ascending major arpeggio of soft bell tones capped by
   *  a rising sparkle. Fired when the player unlocks an achievement. */
  private achieveChime(): void {
    const ac = this.ctx();
    if (!ac) return;
    const t0 = ac.currentTime;
    const master = ac.createGain();
    master.gain.value = clamp01(this.settings.sfx * (SFX_GAIN.achieve ?? 0.6));
    master.connect(ac.destination);
    // a hall reverb (shared impulse with the war sting) so the chime rings out in a bright, celebratory space
    const reverb = ac.createConvolver();
    reverb.buffer = this.impulse(ac);
    const wet = ac.createGain();
    wet.gain.value = 0.32;
    reverb.connect(wet).connect(master);
    // a warm low root under the arpeggio for a little body/weight
    const bass = ac.createOscillator();
    bass.type = 'sine';
    bass.frequency.value = 130.81; // C3
    const bg = ac.createGain();
    bg.gain.setValueAtTime(0.0001, t0);
    bg.gain.exponentialRampToValueAtTime(0.16, t0 + 0.03);
    bg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.7);
    bass.connect(bg).connect(master);
    bass.start(t0);
    bass.stop(t0 + 0.75);
    // a bell voice: triangle fundamental + a quieter octave sine, sent BOTH dry and to the reverb for shimmer
    const bell = (f: number, when: number, peak: number, dur: number): void => {
      const o = ac.createOscillator();
      o.type = 'triangle';
      o.frequency.value = f;
      const o2 = ac.createOscillator();
      o2.type = 'sine';
      o2.frequency.value = f * 2;
      const g = ac.createGain();
      g.gain.setValueAtTime(0.0001, when);
      g.gain.exponentialRampToValueAtTime(peak, when + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
      const g2 = ac.createGain();
      g2.gain.value = 0.22;
      o.connect(g);
      o2.connect(g2).connect(g);
      g.connect(master);
      g.connect(reverb);
      o.start(when);
      o.stop(when + dur + 0.05);
      o2.start(when);
      o2.stop(when + dur + 0.05);
    };
    // bright rising arpeggio C5-E5-G5-C6...
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => bell(f, t0 + i * 0.08, 0.42, 0.5));
    // ...landing on a resolved C-major triad (C6-E6-G6) that rings out longer for a triumphant finish
    const land = t0 + 0.34;
    [1046.5, 1318.51, 1567.98].forEach((f) => bell(f, land, 0.28, 1.15));
    // a rising sparkle over the final chord
    const spark = ac.createOscillator();
    spark.type = 'sine';
    spark.frequency.setValueAtTime(1046.5, land);
    spark.frequency.exponentialRampToValueAtTime(2093, land + 0.24);
    const sg = ac.createGain();
    sg.gain.setValueAtTime(0.0001, land);
    sg.gain.exponentialRampToValueAtTime(0.24, land + 0.04);
    sg.gain.exponentialRampToValueAtTime(0.0001, land + 0.6);
    spark.connect(sg);
    sg.connect(master);
    sg.connect(reverb);
    spark.start(land);
    spark.stop(land + 0.7);
  }

  /** A short SWORD-DRAW "shing" (~0.4s, synthesized): band-passed noise sweeping up (the blade sliding out of the
   *  sheath) capped by a bright metallic ring. Fired when a unit is trained. */
  private recruitSwoosh(): void {
    const ac = this.ctx();
    if (!ac) return;
    const t0 = ac.currentTime;
    const master = ac.createGain();
    master.gain.value = clamp01(this.settings.sfx * (SFX_GAIN.recruit ?? 0.5));
    master.connect(ac.destination);
    // 1. the steel slide: white noise through a band-pass whose centre sweeps up, quick in, medium out
    const noise = ac.createBufferSource();
    noise.buffer = this.noise(ac);
    const bp = ac.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 6;
    bp.frequency.setValueAtTime(1500, t0);
    bp.frequency.exponentialRampToValueAtTime(5200, t0 + 0.22); // the blade clearing the scabbard
    const ng = ac.createGain();
    ng.gain.setValueAtTime(0.0001, t0);
    ng.gain.exponentialRampToValueAtTime(0.5, t0 + 0.02);
    ng.gain.exponentialRampToValueAtTime(0.001, t0 + 0.34);
    noise.connect(bp).connect(ng).connect(master);
    noise.start(t0);
    noise.stop(t0 + 0.4);
    // 2. a bright metallic ring as it clears, two detuned high partials for a steely shimmer
    const ring = (f: number, peak: number): void => {
      const o = ac.createOscillator();
      o.type = 'triangle';
      o.frequency.value = f;
      const g = ac.createGain();
      g.gain.setValueAtTime(0.0001, t0 + 0.15);
      g.gain.exponentialRampToValueAtTime(peak, t0 + 0.17);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.42);
      o.connect(g).connect(master);
      o.start(t0 + 0.15);
      o.stop(t0 + 0.45);
    };
    ring(2637, 0.16); // ~E7
    ring(3520, 0.1); // ~A7, a shimmering pair
  }

  /** A solid CONSTRUCTION thunk (~0.35s, synthesized): a low body thud (mass settling) under two quick woody
   *  hammer knocks. Fired when a building goes up. */
  private buildThunk(): void {
    const ac = this.ctx();
    if (!ac) return;
    const t0 = ac.currentTime;
    const master = ac.createGain();
    master.gain.value = clamp01(this.settings.sfx * (SFX_GAIN.build ?? 0.6));
    master.connect(ac.destination);
    // 1. low body: a sine dropping in pitch, the weight of the structure landing
    const thud = ac.createOscillator();
    thud.type = 'sine';
    thud.frequency.setValueAtTime(150, t0);
    thud.frequency.exponentialRampToValueAtTime(68, t0 + 0.14);
    const tg = ac.createGain();
    tg.gain.setValueAtTime(0.6, t0);
    tg.gain.exponentialRampToValueAtTime(0.001, t0 + 0.3);
    thud.connect(tg).connect(master);
    thud.start(t0);
    thud.stop(t0 + 0.32);
    // 2. two quick woody knocks (hammer on timber): short band-passed noise clacks
    const knock = (when: number, freq: number, peak: number): void => {
      const n = ac.createBufferSource();
      n.buffer = this.noise(ac);
      const bp = ac.createBiquadFilter();
      bp.type = 'bandpass';
      bp.Q.value = 3;
      bp.frequency.value = freq;
      const g = ac.createGain();
      g.gain.setValueAtTime(peak, when);
      g.gain.exponentialRampToValueAtTime(0.0001, when + 0.08);
      n.connect(bp).connect(g).connect(master);
      n.start(when);
      n.stop(when + 0.1);
    };
    knock(t0, 700, 0.34); // first strike, lands with the thud
    knock(t0 + 0.13, 920, 0.26); // a second, higher tap
  }

  /** A dry, crisp CLOCK TICK (~40ms, synthesized): a short high band-passed noise click plus a tiny
   *  triangle blip, the "your time is almost up" pulse fired once per second in a turn timer's last
   *  seconds. Deliberately short and un-startling so five in a row build urgency without being harsh. */
  private clockTick(): void {
    const ac = this.ctx();
    if (!ac) return;
    const t0 = ac.currentTime;
    const master = ac.createGain();
    master.gain.value = clamp01(this.settings.sfx * (SFX_GAIN.tick ?? 0.5));
    master.connect(ac.destination);
    // the click: a high band-passed noise burst with a near-instant decay (the mechanical "tick")
    const click = ac.createBufferSource();
    click.buffer = this.noise(ac);
    const bp = ac.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 6;
    bp.frequency.value = 2100;
    const cg = ac.createGain();
    cg.gain.setValueAtTime(0.6, t0);
    cg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.035);
    click.connect(bp).connect(cg).connect(master);
    click.start(t0);
    click.stop(t0 + 0.05);
    // a faint pitched blip under the click, so the tick reads as a note, not just noise
    const blip = ac.createOscillator();
    blip.type = 'triangle';
    blip.frequency.setValueAtTime(1400, t0);
    const bg = ac.createGain();
    bg.gain.setValueAtTime(0.0001, t0);
    bg.gain.exponentialRampToValueAtTime(0.28, t0 + 0.004);
    bg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.045);
    blip.connect(bg).connect(master);
    blip.start(t0);
    blip.stop(t0 + 0.06);
  }

  /** A bright PURCHASE chime (~0.35s, synthesized): two quick ascending bell dings, a "cha-ching" when a
   *  cosmetic is bought in the shop. */
  /** A soft two-note rising herald: "the table is yours". Rounder and quieter than the coin/achieve
   *  bells on purpose - it plays every single round, so it must inform without ever grating. */
  private yourTurnChime(): void {
    const ac = this.ctx();
    if (!ac) return;
    const t0 = ac.currentTime;
    const master = ac.createGain();
    master.gain.value = clamp01(this.settings.sfx * (SFX_GAIN.yourturn ?? 0.4));
    master.connect(ac.destination);
    const note = (when: number, freq: number, peak: number, dur: number): void => {
      const o = ac.createOscillator(); // round flute-like body
      o.type = 'sine';
      o.frequency.setValueAtTime(freq, when);
      const sparkle = ac.createOscillator(); // a faint octave above for presence over the music bed
      sparkle.type = 'triangle';
      sparkle.frequency.setValueAtTime(freq * 2, when);
      const sg = ac.createGain();
      sg.gain.value = 0.18;
      const g = ac.createGain();
      g.gain.setValueAtTime(0.0001, when);
      g.gain.exponentialRampToValueAtTime(peak, when + 0.02); // gentle attack, not a strike
      g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
      o.connect(g).connect(master);
      sparkle.connect(sg).connect(g);
      o.start(when);
      o.stop(when + dur + 0.02);
      sparkle.start(when);
      sparkle.stop(when + dur + 0.02);
    };
    note(t0, 523.25, 0.5, 0.28); // C5
    note(t0 + 0.13, 783.99, 0.42, 0.42); // G5: the rising fifth reads as "go ahead"
  }

  private coinChime(): void {
    const ac = this.ctx();
    if (!ac) return;
    const t0 = ac.currentTime;
    const master = ac.createGain();
    master.gain.value = clamp01(this.settings.sfx * (SFX_GAIN.coin ?? 0.5));
    master.connect(ac.destination);
    const ding = (when: number, freq: number, peak: number): void => {
      const o = ac.createOscillator(); // the bell body
      o.type = 'triangle';
      o.frequency.setValueAtTime(freq, when);
      const shimmer = ac.createOscillator(); // a soft fifth above for a coin-like sparkle
      shimmer.type = 'sine';
      shimmer.frequency.setValueAtTime(freq * 1.5, when);
      const sg = ac.createGain();
      sg.gain.value = 0.4;
      const g = ac.createGain();
      g.gain.setValueAtTime(0.0001, when);
      g.gain.exponentialRampToValueAtTime(peak, when + 0.008); // fast attack
      g.gain.exponentialRampToValueAtTime(0.0001, when + 0.26); // bright bell decay
      o.connect(g).connect(master);
      shimmer.connect(sg).connect(g);
      o.start(when);
      o.stop(when + 0.28);
      shimmer.start(when);
      shimmer.stop(when + 0.28);
    };
    ding(t0, 880, 0.5); // first bell
    ding(t0 + 0.09, 1320, 0.42); // higher second bell = the "ching"
  }

  // --- settings (persisted; applied live) ---
  getMusicVol(): number {
    return this.settings.music;
  }
  getSfxVol(): number {
    return this.settings.sfx;
  }
  isMuted(): boolean {
    return this.settings.muted;
  }
  setMusicVol(v: number): void {
    this.settings.music = clamp01(v);
    this.persist();
    if (this.cur && !this.settings.muted) this.beds[this.cur].volume = this.settings.music;
  }
  setSfxVol(v: number): void {
    this.settings.sfx = clamp01(v);
    this.persist();
  }
  setMuted(m: boolean): void {
    this.settings.muted = m;
    this.persist();
    // iOS Safari ignores HTMLMediaElement.volume writes (WebKit policy), so mute flips the elements'
    // muted flag too - that one works on every platform. Applied to ALL beds so crossfades stay silent.
    for (const bed of Object.values(this.beds)) bed.muted = m;
    if (this.cur) {
      const bed = this.beds[this.cur];
      if (m) bed.volume = 0;
      else {
        void bed.play().catch(() => {});
        this.rampTo(bed, this.settings.music);
      }
    }
  }
  toggleMuted(): boolean {
    this.setMuted(!this.settings.muted);
    return this.settings.muted;
  }

  private persist(): void {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(this.settings));
    } catch {
      /* storage disabled, settings just won't persist */
    }
  }

  // linear volume ramp using a timer (independent of the render loop / rAF throttling)
  private rampTo(el: HTMLAudioElement, target: number, done?: () => void): void {
    const prev = this.fades.get(el);
    if (prev) window.clearInterval(prev);
    const from = el.volume;
    const steps = Math.max(1, Math.round(FADE_MS / 40));
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      const t = i / steps;
      el.volume = clamp01(from + (target - from) * t);
      if (i >= steps) {
        window.clearInterval(id);
        this.fades.delete(el);
        done?.();
      }
    }, 40);
    this.fades.set(el, id);
  }
}

export const audio = new AudioManager();
